// Re-evaluate a data row's stored `everFlags` against the LIVE Control Strategy
// config. Dependency-free; MIRROR of functions/lib/everFlagsFilter.js (used by the
// one-time cleanup script; the frontend can't import outside src/). A parity test in
// src/util/__tests__/everFlagsLiveConfig.test.js keeps the two in step.
//
// Why: everFlags are written by processDataToDB from the flags computed at the
// time, and frozen once a row is locked. Flags computed from a stale config
// snapshot baked onto the data doc (fixed for new runs in #440) therefore persist
// forever. Arteco Density: 68 "OOC: 9CP" everFlags on locked rows although the
// live config has nineConsecutive Off.

// Flag code (as emitted by functions/calculations/sheetCal/flags.js) → the config
// fields that rule's status is read from. Each entry is a fallback chain in the
// same order as functions/calculations/util/helpers.js (canonical name first,
// legacy config field second). 'Range' is emitted by both the user-defined
// control limit and the tolerance limit rules.
const RULE = {
  threeSigma: ['threeSigma', 'controlLimitViolation'],
  twoSigma: ['twoSigma', 'warningLimitViolation'],
  twoOfThree: ['twoOfThreeTwoSigma', 'twoOfThree'],
  fiveConsecutive: ['fiveConsecutive'],
  nineConsecutive: ['nineConsecutive'],
  sevenConsecutive: ['sevenConsecutive'],
  ewmaTrend: ['ewmaTrend'],
  mrSinglePoint: ['mrSinglePoint', 'singlePointViolation'],
  fiveOver20MR: ['fiveOver20MR', 'fivePointViolation'],
  controlLimit: ['controlLimit', 'controlLimitExceedance'],
  toleranceLimit: ['toleranceLimit', 'toleranceLimitExceedance'],
  adWarning: ['adWarning', 'andersonDarling'],
  tpi: ['tpi', 'tpiOnePointTwo']
};

export const EVER_FLAG_CODE_RULES = {
  UCL: [RULE.threeSigma],
  LCL: [RULE.threeSigma],
  GESD: [RULE.threeSigma],
  'RC>2σ': [RULE.twoSigma],
  'RC<2σ': [RULE.twoSigma],
  '2/3>2σ': [RULE.twoOfThree],
  '2/3<2σ': [RULE.twoOfThree],
  '5CP>1σ': [RULE.fiveConsecutive],
  '5CP<1σ': [RULE.fiveConsecutive],
  '9CP>CL': [RULE.nineConsecutive],
  '9CP<CL': [RULE.nineConsecutive],
  '7CP-I': [RULE.sevenConsecutive],
  '7CP-D': [RULE.sevenConsecutive],
  'E-UCL': [RULE.ewmaTrend],
  'E-LCL': [RULE.ewmaTrend],
  '1>MR': [RULE.mrSinglePoint],
  '5/20>MR': [RULE.fiveOver20MR],
  Range: [RULE.controlLimit, RULE.toleranceLimit],
  AD: [RULE.adWarning],
  TPI: [RULE.tpi]
};

const WARNING = 'Warnings';
const OOC = 'OOC';

// 'off' | 'warning' | 'ooc', or null when the rule is unset/unrecognised.
// Case-insensitive: legacy configs store 'Out of control'.
export const normalizeRuleStatus = (value) => {
  const v = String(value ?? '')
    .trim()
    .toLowerCase();
  if (v === 'off') return 'off';
  if (v === 'warning') return 'warning';
  if (v === 'out of control') return 'ooc';
  return null;
};

const readRule = (config, chain) => {
  for (const field of chain) {
    const value = config[field];
    if (value !== undefined && value !== null && value !== '') return normalizeRuleStatus(value);
  }
  return null;
};

// Severity ('Warnings' | 'OOC') for a code under the live config, null to drop
// it, or `stored` when the config can't say (unknown code / unset rule).
const resolveSeverity = (code, stored, config) => {
  const chains = EVER_FLAG_CODE_RULES[code];
  if (!chains) return stored;
  const statuses = chains.map((chain) => readRule(config, chain));
  if (statuses.includes('ooc')) return OOC;
  if (statuses.includes('warning')) return WARNING;
  if (statuses.every((s) => s === 'off')) return null;
  return stored;
};

// Flatten everFlags (array of strings, or of { version, flags } objects) into
// [{ severity, code }] in stored order, de-duplicated.
const parseEverFlags = (everFlags) => {
  if (!Array.isArray(everFlags)) return [];
  const entries = everFlags
    .map((item) => (typeof item === 'string' ? item : item && item.flags))
    .filter((s) => typeof s === 'string');
  const parsed = [];
  const seen = new Set();
  entries
    .join(';')
    .split(';')
    .forEach((section) => {
      const idx = section.indexOf(':');
      if (idx === -1) return;
      const prefix = section.slice(0, idx).trim();
      let severity = null;
      if (/^(warnings?|w)$/i.test(prefix)) severity = WARNING;
      else if (/^ooc$/i.test(prefix)) severity = OOC;
      if (!severity) return;
      section
        .slice(idx + 1)
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean)
        .forEach((code) => {
          const key = `${severity}|${code}`;
          if (seen.has(key)) return;
          seen.add(key);
          parsed.push({ severity, code });
        });
    });
  return parsed;
};

// Returns the canonical string-array form processDataToDB writes:
// ['Warnings: a, b', 'OOC: c'] (warnings first, empty groups omitted).
// With no live config the stored flags are returned unchanged (normalised).
export const filterEverFlagsByConfig = (everFlags, config) => {
  const groups = { [WARNING]: [], [OOC]: [] };
  parseEverFlags(everFlags).forEach(({ severity, code }) => {
    const resolved = config ? resolveSeverity(code, severity, config) : severity;
    if (resolved && !groups[resolved].includes(code)) groups[resolved].push(code);
  });
  return [WARNING, OOC]
    .filter((severity) => groups[severity].length > 0)
    .map((severity) => `${severity}: ${groups[severity].join(', ')}`);
};
